import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import { render as renderApi } from "../../api/endpoints/render";
import { qk } from "../../api/queryKeys";
import type { PreviewDiff } from "../../api/types/onboardingRun";

function parse(text: string): PreviewDiff | null {
  try {
    const obj = JSON.parse(text) as PreviewDiff;
    if (!obj || !Array.isArray(obj.artifacts)) return null;
    return obj;
  } catch {
    return null;
  }
}

export function PreviewDiffTable({
  sha,
  onReadyChange,
}: {
  sha: string;
  // Reports the sha when the diff is loaded+parsed, else null — so the parent can
  // match it against the CURRENT artifact sha and never apply stale readiness.
  onReadyChange: (readySha: string | null) => void;
}) {
  const q = useQuery({
    queryKey: qk.renderArtifact(sha),
    queryFn: () => renderApi.artifact(sha).then((r) => r.data),
  });
  const diff = q.data != null ? parse(q.data) : null;
  const ready = diff != null;

  useEffect(() => {
    onReadyChange(ready ? sha : null);
  }, [ready, sha, onReadyChange]);

  if (q.isPending) return <p style={{ color: "var(--text-dim)", fontSize: "0.85rem" }}>Loading diff…</p>;
  if (!diff) {
    return (
      <p style={{ color: "var(--crit)", fontSize: "0.85rem" }}>
        Diff evidence unavailable — cannot approve. (See the raw <code>preview-diff.json</code> artifact below.)
      </p>
    );
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
      <thead>
        <tr style={{ textAlign: "left", color: "var(--text-dim)" }}>
          <th style={cell}>Artifact</th><th style={cell}>Change</th>
        </tr>
      </thead>
      <tbody>
        {diff.artifacts.map((a) => (
          <tr key={a.name}>
            <td style={cell}>{a.name}</td>
            <td style={cell}>{a.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const cell: React.CSSProperties = { padding: "4px 8px", borderBottom: "1px solid var(--border)" };
